import { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
// Card components removed – using styled divs instead
import { usePreferences } from '../../contexts/PreferencesContext';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  ArrowLeft,
  Save,
  Send,
  X,
  Loader2,
  Check,
  Clock,
} from 'lucide-react';
import { coreAPI, onboardingAPI } from '../../lib/api';
import type { Site } from '../../lib/api';
import type { SupplierRelatedSite } from './wizard/WizardStep4SiteAccess';
import { toast } from 'sonner';

import {
  WIZARD_STEPS,
  STATUS_LABELS,
  DEFAULT_RISK_QUESTIONS,
  validateWizardStep,
} from './wizard/types';
import type { SupplierFormData } from './wizard/types';

import WizardStep1Group from './wizard/WizardStep1Group';
import WizardStep2Details from './wizard/WizardStep2Details';
import WizardStep3Profile from './wizard/WizardStep3Profile';
import WizardStep4SiteAccess from './wizard/WizardStep4SiteAccess';
import WizardStep5Risk from './wizard/WizardStep5Risk';
import WizardModals from './wizard/WizardModals';
import WizardSidebar from './wizard/WizardSidebar';
import { useCanApproveSuppliers } from '../../hooks/useCanApproveSuppliers';
import { useWizardActions } from './wizard/useWizardActions';
import { useExistingSupplierSelection } from './wizard/useExistingSupplierSelection';
import { useIntercompanyEligibility } from './wizard/useIntercompanyEligibility';
import { loadSupplierData as loadSupplierDataFn } from './wizard/loadSupplierData';

export default function SupplierOnboardingWizard() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { preferences } = usePreferences();
  const isNewSupplier = id === 'new';

  // Persist step in URL so refresh keeps progress
  const stepFromUrl = Number(searchParams.get('step')) || 1;

  const [loading, setLoading] = useState(!isNewSupplier);
  const [saving, setSaving] = useState(false);
  // New suppliers always start at step 1 regardless of URL param
  const [currentStep, setCurrentStepRaw] = useState(isNewSupplier ? 1 : stepFromUrl);
  // CR-3942: Progressive step unlock — new suppliers always start at 0, existing suppliers restore from URL
  const [highestCompletedStep, setHighestCompletedStep] = useState(isNewSupplier ? 0 : Math.max(0, stepFromUrl - 1));

  // Keep the state updater pure — URL sync happens in the effect below.
  // Calling setSearchParams (a Router state update) from inside a setState
  // updater triggers React's "Cannot update a component while rendering a
  // different component" warning, so the two concerns are separated.
  const setCurrentStep = (valOrFn: number | ((prev: number) => number)) => {
    setCurrentStepRaw(prev => (typeof valOrFn === 'function' ? valOrFn(prev) : valOrFn));
  };

  // Persist the active step to the URL so a refresh keeps progress. Runs as
  // a post-render effect rather than inside the setCurrentStep updater.
  useEffect(() => {
    setSearchParams({ step: String(currentStep) }, { replace: true });
    // setSearchParams is stable across renders; depend only on the step.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep]);
  const [supplierStatus, setSupplierStatus] = useState<string>('draft');
  const [previewMode, setPreviewMode] = useState(false);
  const [workflowId, setWorkflowId] = useState<number | null>(null);
  // Track supplier ID after creation to prevent duplicate creation on retry
  const [createdSupplierId, setCreatedSupplierId] = useState<number | null>(null);

  // Modals
  const [showDeclineModal, setShowDeclineModal] = useState(false);
  const [showApproveModal, setShowApproveModal] = useState(false);

  // CR-3942: Create New Group modal
  const [showCreateGroupModal, setShowCreateGroupModal] = useState(false);
  const [newGroupName, setNewGroupName] = useState('');

  // Decline/Approve form data
  const [declineReason, setDeclineReason] = useState('');
  const [declineNotes, setDeclineNotes] = useState('');
  const [approvalLevel, setApprovalLevel] = useState('');

  // CR-3942: Supplier groups from API
  const [supplierGroups, setSupplierGroups] = useState<Array<{ id: number; name: string }>>([]);

  // Customer sites (Company = Site, not rollup level)
  const [customerSites, setCustomerSites] = useState<Site[]>([]);

  // Sibling supplier companies in the selected supplier group. Step 4
  // ("Supplier User Related Companies") MUST source from supplier-side
  // hierarchy only — never customer companies. The new supplier itself
  // is added as a synthetic entry in Step 4 since it has no id yet.
  const [supplierGroupSites, setSupplierGroupSites] = useState<SupplierRelatedSite[]>([]);

  // Form data
  const [formData, setFormData] = useState<SupplierFormData>({
    is_part_of_group: null,
    // CR-6696: primary company for the supplier user (group suppliers only).
    // 0 = the new supplier itself — the pre-CR default.
    primary_company_id: 0,
    company_name: '',
    company_address: '',
    // CR-5390: structured address fields (postcode + line 1 are mandatory).
    address_line_1: '',
    address_line_2: '',
    address_city: '',
    address_region: '',
    address_postcode: '',
    address_country: '',
    company_email: '',
    currency: preferences.currency || 'GBP',
    annual_spend: '',
    description: '',
    customer_ids: [],
    customer_settings: {},
    // CR-5877: routing config — defaults to null so the buyer must choose
    // explicitly. validateWizardStep blocks Save & Continue until set.
    risk_routing_level: null,
    risk_routing_site_scope: null,
    risk_routing_group_id: null,
    risk_routing_site_id: null,
    sites: [],
    contact_title: '',
    contact_first_name: '',
    contact_last_name: '',
    contact_email: '',
    contact_phone: '',
    contact_phone_extension: '',
    contact_job_title: '',
    contact_department: '',
    contact_login_type: 'supplier',
    contact_linkedin: '',
    additional_contacts: [],
    related_companies: [],
    risk_answers: {},
  });

  // Dynamic risk questions from onboarding datasets API
  const [riskQuestions, setRiskQuestions] = useState<Array<{
    id: string;
    question: string;
    type: string;
    options: string[];
    scores?: Record<string, number>;
    max_score?: number;
  }>>([]);
  const [riskDatasetId, setRiskDatasetId] = useState<number | null>(null);
  const [loadingRiskQuestions, setLoadingRiskQuestions] = useState(false);

  // CR-3942: Load supplier groups from API.
  // Filter to supplier-typed groups only — the unfiltered endpoint
  // returns customer groups too, which would let an admin link a new
  // supplier into a customer group and breach tenant separation.
  useEffect(() => {
    const loadGroups = async () => {
      try {
        const { coreAPI } = await import('../../lib/api');
        const response = await coreAPI.getCompanyGroups({ company_type: 'supplier' });
        const groups = response.data.results || response.data || [];
        setSupplierGroups(groups.map((g: { id: number; name: string }) => ({ id: g.id, name: g.name })));
      } catch (err) {
        console.error('Failed to load supplier groups:', err);
      }
    };
    loadGroups();
  }, []);

  // Load accessible sites for the Customer dropdown (Company = Site)
  useEffect(() => {
    const loadSites = async () => {
      try {
        const response = await coreAPI.getAccessibleSites();
        const sites = (Array.isArray(response.data) ? response.data : (response.data as unknown as { results: Site[] }).results) || [];
        setCustomerSites(sites);
      } catch (err) {
        console.error('Failed to load customer sites:', err);
      }
    };
    loadSites();
  }, []);

  // Load supplier-group sibling companies whenever the chosen group changes.
  // Standalone suppliers (is_part_of_group=false) get an empty list and
  // Step 4 falls back to the synthetic new-supplier entry alone.
  useEffect(() => {
    if (formData.is_part_of_group !== true || !formData.supplier_group_id) {
      setSupplierGroupSites([]);
      return;
    }
    coreAPI.getCompanyGroupMembers(formData.supplier_group_id)
      .then((res) => {
        const members = res.data?.companies || [];
        const supplierMembers = members
          .filter((c) => c.company_type === 'supplier' && c.is_active !== false)
          .map((c) => ({
            id: c.id,
            name: c.name,
            city: c.city,
            country: c.country,
          }));
        setSupplierGroupSites(supplierMembers);
        // CR-6696: if the group changed, a previously chosen Primary Company
        // may no longer be a member — fall back to the new supplier (id 0).
        const validIds = new Set([0, ...supplierMembers.map((m) => m.id)]);
        setFormData(prev => (
          prev.primary_company_id != null && !validIds.has(prev.primary_company_id)
            ? { ...prev, primary_company_id: 0, primary_company_name: undefined }
            : prev
        ));
      })
      .catch((err) => {
        console.error('Failed to load supplier group members:', err);
        setSupplierGroupSites([]);
      });
  }, [formData.is_part_of_group, formData.supplier_group_id]);

  // CR-5877: Derive the legacy "primary customer_id" from the explicit routing
  // configuration. We no longer infer it from selection order; instead it
  // tracks whichever site/group the buyer chose to receive risk questions.
  // The risk-questions API and create-from-wizard endpoint still key off
  // customer_id, so this keeps them functional while honouring the new rule.
  useEffect(() => {
    let resolvedSiteId: number | undefined;
    let resolvedSiteName = '';
    const linked = formData.customer_ids || [];
    const linkedSites = linked
      .map((sid) => customerSites.find((s) => s.id === sid))
      .filter((s): s is Site => !!s);

    if (formData.risk_routing_level === 'site'
        && formData.risk_routing_site_scope === 'single'
        && formData.risk_routing_site_id) {
      const target = linkedSites.find((s) => s.id === formData.risk_routing_site_id);
      if (target) {
        resolvedSiteId = target.id;
        resolvedSiteName = target.name;
      }
    } else if ((formData.risk_routing_level === 'group'
                || (formData.risk_routing_level === 'site'
                    && formData.risk_routing_site_scope === 'all'))
               && formData.risk_routing_group_id) {
      // Datasets are stored per-company, so represent the group by the first
      // linked site within it. The relationship table already carries every
      // selected site, so questionnaires can fan out across the group from
      // there if needed.
      const target = linkedSites.find((s) => s.group_id === formData.risk_routing_group_id);
      if (target) {
        resolvedSiteId = target.id;
        resolvedSiteName = target.name;
      }
    }

    if (resolvedSiteId !== formData.customer_id) {
      setFormData(prev => ({
        ...prev,
        customer_id: resolvedSiteId,
        customer_name: resolvedSiteName,
      }));
    }
  }, [
    formData.risk_routing_level,
    formData.risk_routing_site_scope,
    formData.risk_routing_group_id,
    formData.risk_routing_site_id,
    formData.customer_ids,
    formData.customer_id,
    customerSites,
  ]);

  // Load risk questions dynamically when customer is selected, with default fallback
  useEffect(() => {
    if (!formData.customer_id) {
      // No customer selected — show defaults so Step 5 is never empty
      setRiskQuestions(DEFAULT_RISK_QUESTIONS);
      setRiskDatasetId(null);
      return;
    }
    const loadRiskQuestions = async () => {
      setLoadingRiskQuestions(true);
      try {
        const response = await onboardingAPI.getRiskQuestions(formData.customer_id as number);
        const data = response.data;
        if (data.questions && data.questions.length > 0) {
          setRiskDatasetId(data.dataset_id);
          setRiskQuestions(
            data.questions.map((q) => ({
              id: q.id,
              question: q.question,
              type: q.type,
              options: q.options,
            }))
          );
        } else {
          // API returned no questions — use defaults
          setRiskDatasetId(null);
          setRiskQuestions(DEFAULT_RISK_QUESTIONS);
        }
      } catch (err) {
        console.error('Failed to load risk questions:', err);
        // Fallback to defaults on error
        setRiskQuestions(DEFAULT_RISK_QUESTIONS);
        setRiskDatasetId(null);
      } finally {
        setLoadingRiskQuestions(false);
      }
    };
    loadRiskQuestions();
  }, [formData.customer_id]);

  // Load existing supplier data
  useEffect(() => {
    if (!isNewSupplier && id) {
      loadSupplierDataFn({
        id,
        setLoading,
        setCustomerSites,
        setSupplierStatus,
        setPreviewMode,
        setFormData,
        setCurrentStep,
        setHighestCompletedStep,
        setWorkflowId,
        urlStepOverride: stepFromUrl,
      });
    }
  }, [id, isNewSupplier]);

  const handleInputChange = (field: keyof SupplierFormData, value: string | number | boolean | null | string[] | number[] | Record<string, unknown>) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // CR-6366 alignment: update one linked company's per-site supplier code /
  // currency. New rows default their currency to the top-level currency so the
  // select always shows a sensible value; the buyer can override per site.
  const handleCustomerSettingChange = (
    id: number,
    patch: Partial<{ supplier_code: string; currency: string }>,
  ) => {
    setFormData(prev => {
      const map = prev.customer_settings || {};
      const cur = map[id] || { supplier_code: '', currency: prev.currency || 'GBP' };
      return { ...prev, customer_settings: { ...map, [id]: { ...cur, ...patch } } };
    });
  };

  const handleRiskAnswerChange = (questionId: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      risk_answers: { ...prev.risk_answers, [questionId]: value },
    }));
  };

  // CR-3942: Step 1 is complete when user answered No, or answered Yes AND selected a group
  const isStep1Complete = formData.is_part_of_group === false ||
    (formData.is_part_of_group === true && !!formData.supplier_group_id && formData.supplier_group_id > 0);

  // CR-3942: Handle Create New Group
  const handleCreateGroup = async () => {
    const trimmed = newGroupName.trim();
    if (!trimmed) {
      toast.error('Please enter a group name.');
      return;
    }
    try {
      const { coreAPI } = await import('../../lib/api');
      const response = await coreAPI.createCompanyGroup({ name: trimmed });
      const created = response.data;
      setSupplierGroups(prev => [...prev, { id: created.id, name: created.name }]);
      handleInputChange('supplier_group_id', created.id);
      handleInputChange('supplier_group_name', created.name);
      setShowCreateGroupModal(false);
      setNewGroupName('');
      toast.success(`Group "${trimmed}" created successfully.`);
    } catch {
      toast.error('Failed to create group. Please try again.');
    }
  };

  const validateStep = (step: number): string | null => validateWizardStep(step, formData);

  // CR-4141 rework 2: the wizard header is reachable from Pending Due Diligence
  // via "View Full Profile", so a Buyer landed on Approve/Decline/Send Invite
  // controls their role cannot use. Hide the whole action group for them; the
  // backend returns 403 on the same endpoints.
  const canApproveSuppliers = useCanApproveSuppliers();

  // CR-6695: Default Login Type restriction — 'customer' is only offered for
  // intercompany suppliers (platform-determined from the group relationship).
  const isIntercompany = useIntercompanyEligibility(formData, setFormData);

  // CR-3942: Handle sidebar step navigation with validation of current step
  const handleStepNavigation = (targetStep: number) => {
    // Going backwards is always allowed
    if (targetStep < currentStep) {
      setCurrentStep(targetStep);
      saveDraft(targetStep);
      return;
    }
    // Going forward to an already-reached step: skip validation
    // (user may have incomplete data on current step but already visited the target before)
    if (targetStep <= highestCompletedStep + 1) {
      setCurrentStep(targetStep);
      saveDraft(targetStep);
      return;
    }
    // Going forward beyond highest reached: validate current step first
    const error = validateStep(currentStep);
    if (error) {
      toast.error(error);
      return;
    }
    setCurrentStep(targetStep);
    saveDraft(targetStep);
  };

  // CR-6685: existing-supplier detection for the Step 2 name typeahead — pick a
  // match to link an existing supplier instead of creating a duplicate.
  const {
    existingSupplierId,
    selectExisting,
    changeSupplierName,
    clearExisting,
    lockedField,
  } = useExistingSupplierSelection(setFormData);

  const {
    saveDraft,
    handleSave,
    handleDecline,
    handleApprove,
    handleSendInvite,
  } = useWizardActions({
    id,
    isNewSupplier,
    formData,
    currentStep,
    highestCompletedStep,
    createdSupplierId,
    existingSupplierId,
    workflowId,
    riskDatasetId,
    riskQuestions,
    declineReason,
    declineNotes,
    setSaving,
    setCreatedSupplierId,
    setSupplierStatus,
    setHighestCompletedStep,
    setCurrentStep,
    setShowDeclineModal,
    setShowApproveModal,
    validateStep,
  });

  // Supplier-side companies for Steps 3 & 4: the new supplier itself
  // (synthetic, id=0) plus any sibling companies in the selected supplier
  // group. CR-6696: Step 3's Primary Company dropdown offers this same list
  // so the choice always matches a Step 4 card.
  const supplierSites: SupplierRelatedSite[] = [
    {
      id: 0,
      name: formData.company_name?.trim() || 'New Supplier',
      city: formData.address_city,
      country: formData.address_country,
      isNew: true,
    },
    ...supplierGroupSites,
  ];

  // Calculate progress
  const completedAnswers = Object.keys(formData.risk_answers).length;
  const totalQuestions = riskQuestions.length;

  // Compute per-step validity so sidebar shows accurate completion status
  const stepValidity: Record<number, boolean> = {};
  for (let s = 1; s <= WIZARD_STEPS.length; s++) {
    stepValidity[s] = validateStep(s) === null;
  }

  // Calculate risk level from scored questions
  const scoredQuestions = riskQuestions.filter(q => q.scores && formData.risk_answers[q.id]);
  let riskLevel: string | undefined;
  let riskScore: number | undefined;
  if (scoredQuestions.length > 0) {
    const totalScore = scoredQuestions.reduce((sum, q) => sum + (q.scores?.[formData.risk_answers[q.id]] || 0), 0);
    const maxPossible = scoredQuestions.reduce((sum, q) => sum + (q.max_score || 10), 0);
    riskScore = maxPossible > 0 ? Math.round((totalScore / maxPossible) * 100) : 0;
    riskLevel = riskScore >= 66 ? 'high' : riskScore >= 33 ? 'medium' : 'low';
  } else if (completedAnswers > 0) {
    // Default questions without scores - treat as low risk when all answered
    riskLevel = 'low';
    riskScore = 0;
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  const statusConfig = STATUS_LABELS[supplierStatus];
  const StatusIcon = statusConfig?.icon || Clock;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => { saveDraft(); navigate(isNewSupplier ? '/suppliers' : '/onboarding'); }}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          <div>
            <h1 className="text-2xl font-bold">
              {isNewSupplier ? 'Add New Supplier' : 'Supplier Onboarding'}
            </h1>
            {!isNewSupplier && statusConfig && (
              <div className="flex items-center gap-2 mt-1">
                <Badge className={statusConfig.color}>
                  <StatusIcon className="h-3 w-3 mr-1" />
                  {statusConfig.label}
                </Badge>
              </div>
            )}
          </div>
        </div>

        {/* Action Buttons based on status */}
        <div className="flex gap-2">
          {/* Submit for Approval moved to bottom nav on Step 5 */}
          {canApproveSuppliers && supplierStatus === 'pending_initial' && (
            <>
              <Button variant="destructive" onClick={() => setShowDeclineModal(true)}>
                <X className="h-4 w-4 mr-2" />
                Decline
              </Button>
              <Button onClick={handleSendInvite} disabled={saving}>
                <Send className="h-4 w-4 mr-2" />
                Send Invite
              </Button>
              <Button variant="default" onClick={() => setShowApproveModal(true)}>
                <Check className="h-4 w-4 mr-2" />
                Approve
              </Button>
            </>
          )}
          {/* 2026-09-04: no Approve/Decline at 'due_diligence' — the decision
              is made on the approval page (questionnaire review), so offering
              it here was redundant and its workflow call 400'd anyway. */}
        </div>
      </div>

      {/* Wizard Container - LEFT-RIGHT Layout */}
      <div className="flex gap-0 border rounded-lg overflow-hidden bg-white">
        {/* LEFT Panel - Wizard Navigation */}
        <WizardSidebar
          currentStep={currentStep}
          highestCompletedStep={highestCompletedStep}
          onStepNavigation={handleStepNavigation}
          completedAnswers={completedAnswers}
          totalQuestions={totalQuestions}
          stepValidity={stepValidity}
          annualSpend={formData.annual_spend}
          riskLevel={riskLevel}
          riskScore={riskScore}
          currency={formData.currency}
        />

        {/* RIGHT Panel - Form Content */}
        <div className="flex-1 p-8">
          {/* Step 1: Supplier Group (CR-3942) */}
          {currentStep === 1 && (
            <WizardStep1Group
              formData={formData}
              onInputChange={handleInputChange}
              previewMode={previewMode}
              supplierGroups={supplierGroups}
              onShowCreateGroupModal={() => setShowCreateGroupModal(true)}
            />
          )}

          {/* Step 2: Supplier Details (CR-3939, CR-3943, CR-3944) */}
          {currentStep === 2 && (
            <WizardStep2Details
              formData={formData}
              onInputChange={handleInputChange}
              previewMode={previewMode}
              customerSites={customerSites}
              // CR-6685: only new suppliers can detect/link an existing one —
              // editing an existing draft is already bound to its supplier.
              existingSearchEnabled={isNewSupplier && !previewMode}
              existingSupplierId={existingSupplierId}
              onSelectExisting={selectExisting}
              onSupplierNameChange={changeSupplierName}
              onClearExisting={clearExisting}
              lockedField={lockedField}
              onCustomerSettingChange={handleCustomerSettingChange}
            />
          )}

          {/* Step 3: Supplier User Profile (CR-3947,3948,3949,3950,3951,3952) */}
          {currentStep === 3 && (
            <WizardStep3Profile
              formData={formData}
              setFormData={setFormData}
              onInputChange={handleInputChange}
              previewMode={previewMode}
              groupCompanies={supplierSites}
              isIntercompany={isIntercompany}
            />
          )}

          {/* Step 4: User Site Access (Related Sites style).
              Supplier-side companies only — the new supplier itself plus
              any sibling sites in the same supplier group. Never customer
              companies (those live on Step 2). */}
          {currentStep === 4 && (
            <WizardStep4SiteAccess
              formData={formData}
              onInputChange={handleInputChange}
              previewMode={previewMode}
              supplierSites={supplierSites}
            />
          )}

          {/* Step 5: Risk Questions / Approval Stage */}
          {currentStep === 5 && (
            <WizardStep5Risk
              formData={formData}
              onRiskAnswerChange={handleRiskAnswerChange}
              previewMode={previewMode}
              riskQuestions={riskQuestions}
              loadingRiskQuestions={loadingRiskQuestions}
            />
          )}

          {/* Navigation Buttons */}
          <div className="flex justify-between mt-8 pt-6 border-t">
            <Button
              variant="outline"
              onClick={() => setCurrentStep(prev => Math.max(1, prev - 1))}
              disabled={currentStep === 1}
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Previous
            </Button>

            {currentStep < WIZARD_STEPS.length ? (
              // CR-3942: Hide Save & Continue when Step 1 is not complete
              currentStep === 1 && !isStep1Complete ? null : (
                <Button
                  onClick={handleSave}
                  disabled={saving}
                >
                  {saving ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Save className="h-4 w-4 mr-2" />
                  )}
                  Save & Continue
                </Button>
              )
            ) : (
              <Button onClick={handleSave} disabled={saving || previewMode}>
                {saving ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Send className="h-4 w-4 mr-2" />
                )}
                Submit for Approval
              </Button>
            )}
          </div>
        </div>
      </div>

      <WizardModals
        showDeclineModal={showDeclineModal}
        setShowDeclineModal={setShowDeclineModal}
        declineReason={declineReason}
        setDeclineReason={setDeclineReason}
        declineNotes={declineNotes}
        setDeclineNotes={setDeclineNotes}
        handleDecline={handleDecline}
        saving={saving}
        showApproveModal={showApproveModal}
        setShowApproveModal={setShowApproveModal}
        approvalLevel={approvalLevel}
        setApprovalLevel={setApprovalLevel}
        handleApprove={handleApprove}
        showCreateGroupModal={showCreateGroupModal}
        setShowCreateGroupModal={setShowCreateGroupModal}
        newGroupName={newGroupName}
        setNewGroupName={setNewGroupName}
        handleCreateGroup={handleCreateGroup}
      />

    </div>
  );
}
